/**
 * Reset to Graphic Standard — enforce a template's saved standards onto the
 * current card.
 *
 * Opens when "Reset to Graphic Standard" is clicked (behind the `enableApplyStandardsPreview`
 * flag). Read-only before/after preview: Before = the card's live current value,
 * After = the template's saved standard. Confirming pushes the standards onto the
 * element (the parent calls `applyTemplateStandardsToElement`); it does NOT change
 * the template. This is the inverse of Set as Graphic Standard.
 *
 * Pure presentational component — it renders precomputed rows and signals confirm.
 */

import {
  DialogCard,
  DialogFooter,
  DialogHeader,
  DialogText,
  DialogTitle,
  PrimaryButton,
  SecondaryButton,
} from '@src/components/common/dialogStyles';
import Modal from '@src/components/common/Modal';

import {
  asText,
  ColumnHead,
  DiffScroll,
  displayValue,
  Grid,
  LabelCell,
  Row,
  rowKey,
  Swatch,
  ValueCell,
} from './standardsDiffTable';

import type { ControlDiffRow } from '@src/utility/compositionSettings';
import type { FC } from 'react';

type Props = {
  rows: ControlDiffRow[];
  onCancel: () => void;
  onConfirm: () => void;
};

const COLUMNS = 'minmax(110px, 0.9fr) 1fr 1fr';

export const ApplyStandardsModal: FC<Props> = ({
  rows,
  onCancel,
  onConfirm,
}) => {
  const changedCount = rows.filter(r => r.changed).length;

  return (
    <Modal isOpen closeModal={onCancel} backgroundColor="rgba(0, 0, 0, 0.75)">
      <DialogCard $size="lg">
        <DialogHeader>
          <DialogTitle>Reset to Graphic Standard</DialogTitle>
          <DialogText>
            {changedCount > 0
              ? "Reset this card back to the graphic's saved standard. Here's what will change."
              : 'This card already matches the graphic standard — nothing will change.'}
          </DialogText>
        </DialogHeader>

        <DiffScroll>
          <Grid $columns={COLUMNS}>
            <ColumnHead />
            <ColumnHead>Current</ColumnHead>
            <ColumnHead>Standard</ColumnHead>

            {rows.map(row => (
              <Row key={rowKey(row.control)}>
                <LabelCell $changed={row.changed}>{row.label}</LabelCell>
                <ValueCell $changed={row.changed} $muted>
                  {row.control.type === 'color' ? (
                    <Swatch $color={asText(row.before)} />
                  ) : null}
                  {displayValue(row, row.before)}
                </ValueCell>
                <ValueCell $changed={row.changed}>
                  {row.control.type === 'color' ? (
                    <Swatch $color={asText(row.after)} />
                  ) : null}
                  {displayValue(row, row.after)}
                </ValueCell>
              </Row>
            ))}
          </Grid>
        </DiffScroll>

        <DialogFooter>
          <SecondaryButton type="button" onClick={onCancel}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="button"
            onClick={onConfirm}
            disabled={changedCount === 0}
          >
            Reset to Graphic Standard
          </PrimaryButton>
        </DialogFooter>
      </DialogCard>
    </Modal>
  );
};

export default ApplyStandardsModal;
